import React, { useState } from "react";
import {
    ArrowRightAltOutlined,
    ContactEmergencyOutlined,
    Edit,
    Add,
} from "@mui/icons-material";
import { Button, Grid, MenuItem } from "@mui/material";
import CustomCrudDialog from "../../../components/dialogs/CustomCrudDialog";
import { useDispatch, useSelector } from "react-redux";
import useRTK from "../../../hooks/useRTK";
import { Form, Formik } from "formik";
import CustomSubmitButton1 from "../../../components/forms/buttons/CustomSubmitButton1";
import CustomTextField from "../../../components/forms/CustomTextField";
import * as Yup from "yup";
import { colors } from "../../../assets/utils/colors";
import { useUpdateCompanyContactsMutation } from "../../../api/contactsAPI/contactsAPI";
import { setMemberProfile } from "../../../state/features/profile/memberProfileSlice";
import { isEmpty } from "lodash";
import { adminSelector } from "../../../state/features/selectors";
import CustomSelect from "../../../components/forms/CustomSelect";

// ################## VALIDATION ####################
const contactsValidationSchema = Yup.object().shape({
    postalAddress: Yup.string().required("postal address is required field"),
    location: Yup.string().required(),
    street: Yup.string().required(),
    ward: Yup.string().required(),
    district: Yup.string().required(),
    phoneNumber: Yup.string()
        .required("Phone number is required field")
        .matches(
            /(?=.*?\d)^\$?(([1-9]\d{0,2}(,\d{3})*)|\d+)?(\.\d{1,2})?$/,
            "Positions must be a number"
        )
        .max(10, "Phone number must be at most 10 characters")
        .min(10, "Phone number must be at least 10 characters"),
    region: Yup.string().required(),
    plotNumber: Yup.string().required("plot number is required field"),
    email: Yup.string().required(),
    fax: Yup.string().nullable(),
    website: Yup.string().nullable(),
});

const AddCompanyContacts = ({ contacts }) => {
    // ################# CONST ##################
    const RTK = useRTK();
    const dispatch = useDispatch();

    // ############## Comp State ##############
    const [openDialog, setOpenDialog] = useState(false);

    // ############# Func ###############
    const handleOpenDialog = () => {
        setOpenDialog(true);
    };
    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    // ############# Redux ################
    const { regions } = useSelector(adminSelector);

    // ############## RTK ##################
    const [
        updateCompanyContacts,
        { isLoading, isSuccess, isError, error, data: updatedData },
    ] = useUpdateCompanyContactsMutation();

    // ############### useEffect ################
    // => RTK action
    const rtkActions = (data) => {
        handleCloseDialog();
        dispatch(setMemberProfile(data));
    };
    RTK.useRTKResponse(isSuccess, isError, error, updatedData, rtkActions);

    return (
        <>
            <CustomCrudDialog
                openDialog={openDialog}
                closeDialog={handleCloseDialog}
                head={
                    isEmpty(contacts)
                        ? "Add company contacts"
                        : "Update company contacts"
                }
            >
                <Formik
                    enableReinitialize
                    initialValues={{
                        postalAddress: !isEmpty(contacts)
                            ? contacts.postalAddress
                            : "",
                        location: !isEmpty(contacts) ? contacts.location : "",
                        street: !isEmpty(contacts) ? contacts.street : "",
                        ward: !isEmpty(contacts) ? contacts.ward : "",
                        district: !isEmpty(contacts) ? contacts.district : "",
                        phoneNumber: !isEmpty(contacts)
                            ? contacts.phoneNumber
                            : "",
                        region: !isEmpty(contacts) ? contacts.region?.id : "",
                        plotNumber: !isEmpty(contacts)
                            ? contacts.plotNumber
                            : "",
                        email: !isEmpty(contacts) ? contacts.email : "",
                        fax: !isEmpty(contacts) ? contacts.fax : "",
                        website: !isEmpty(contacts) ? contacts.website : "",
                    }}
                    validationSchema={contactsValidationSchema}
                    onSubmit={(payload) => {
                        updateCompanyContacts(payload);
                    }}
                >
                    {(formik) => (
                        <Form>
                            <Grid
                                container
                                justifyContent={"center"}
                                columnSpacing={2}
                            >
                                <Grid item sm={6} xs={6}>
                                    <CustomTextField
                                        type="text"
                                        name="location"
                                        label={"Location"}
                                        bBottom={colors.secondary}
                                    />
                                </Grid>
                                <Grid item sm={6} xs={6}>
                                    <CustomTextField
                                        type="text"
                                        name="street"
                                        label={"Street"}
                                        bBottom={colors.secondary}
                                    />
                                </Grid>
                                <Grid item sm={4} xs={6}>
                                    <CustomTextField
                                        type="text"
                                        name="postalAddress"
                                        label={"Postal address"}
                                        bBottom={colors.secondary}
                                    />
                                </Grid>
                                <Grid item sm={4} xs={6}>
                                    <CustomTextField
                                        type="text"
                                        name="plotNumber"
                                        label={"Plot number"}
                                        bBottom={colors.secondary}
                                    />
                                </Grid>
                                <Grid item sm={4} xs={6}>
                                    <CustomTextField
                                        type="text"
                                        name="fax"
                                        label={"Fax"}
                                        optional
                                        bBottom={colors.secondary}
                                    />
                                </Grid>
                                <Grid item sm={4} xs={6}>
                                    {/* <CustomTextField
                                        type="text"
                                        name="region"
                                        label={"Region"}
                                        bBottom={colors.secondary}
                                    /> */}
                                    <CustomSelect
                                        label={"Region"}
                                        formik={formik}
                                        bBottom={colors.secondary}
                                        name="region"
                                    >
                                        {regions?.map((region) => (
                                            <MenuItem
                                                value={region?.id}
                                                key={region?.id}
                                            >
                                                {region?.regionName}
                                            </MenuItem>
                                        ))}
                                    </CustomSelect>
                                </Grid>
                                <Grid item sm={4} xs={6}>
                                    <CustomTextField
                                        type="text"
                                        name="district"
                                        label={"District"}
                                        bBottom={colors.secondary}
                                    />
                                </Grid>
                                <Grid item sm={4} xs={6}>
                                    <CustomTextField
                                        type="text"
                                        name="ward"
                                        label={"Ward"}
                                        bBottom={colors.secondary}
                                    />
                                </Grid>
                                <Grid item sm={4} xs={12}>
                                    <CustomTextField
                                        type="text"
                                        name="phoneNumber"
                                        label={"Phone number"}
                                        bBottom={colors.secondary}
                                    />
                                </Grid>
                                <Grid item sm={4} xs={12}>
                                    <CustomTextField
                                        type="text"
                                        name="email"
                                        label={"Email"}
                                        bBottom={colors.secondary}
                                    />
                                </Grid>
                                <Grid item sm={4} xs={12}>
                                    <CustomTextField
                                        type="text"
                                        name="website"
                                        label={"Website"}
                                        optional
                                        bBottom={colors.secondary}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item sm={12} xs={12}>
                                <CustomSubmitButton1
                                    loading={isLoading}
                                    btnColor={"primary"}
                                    startIcon={<ContactEmergencyOutlined />}
                                    endIcon={<ArrowRightAltOutlined />}
                                    onClick={formik.handleSubmit}
                                >
                                    Update company contacts
                                </CustomSubmitButton1>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </CustomCrudDialog>
            <Button
                size="small"
                startIcon={isEmpty(contacts) ? <Add /> : <Edit />}
                variant="outlined"
                sx={{
                    fontSize: 12,
                    fontWeight: "bold",
                    color: "#fff",
                    backgroundImage: `linear-gradient(rgba(23, 148, 202, .5), rgba(23, 148, 202, .5)), radial-gradient(circle at left top, rgb(1, 110, 218), rgb(49, 180, 76, .5))`,
                }}
                onClick={() => {
                    handleOpenDialog();
                }}
            >
                {isEmpty(contacts) ? " Add" : "Update"}
            </Button>
        </>
    );
};

export default AddCompanyContacts;
